// src/components/navigation/BottomNav.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BottomNavigation, BottomNavigationAction, Paper, Badge, Portal } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";
import OndemandVideoRoundedIcon from "@mui/icons-material/OndemandVideoRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import MailRoundedIcon from "@mui/icons-material/MailRounded";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import { APP_SHELL_LAYOUT } from "../../constants/constants";
import { useUserBadges } from "../../contexts/UserBadgesContext";

const BottomNav = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const { unreadCount } = useUserBadges();
  const hasUnreadMessages = unreadCount > 0;
  const unreadBadge = unreadCount > 99 ? "99+" : unreadCount;
  const isWizardRoute = location.pathname.startsWith("/timecapsule");

  // Update the bottom nav value based on the current route
  useEffect(() => {
    const path = location.pathname;
    if (path === "/") {
      setValue(0);
    } else if (path.includes("/watch") || path.includes("/series")) {
      setValue(1);
    } else if (path.includes("/messages")) {
      setValue(2);
    } else if (path.includes("/history")) {
      setValue(3);
    } else if (path.includes("/settings")) {
      setValue(4);
    }
  }, [location.pathname]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (newValue === 0) {
      window.dispatchEvent(new CustomEvent("inssa:home-reset-map-filters"));
      navigate("/");
    } else if (newValue === 1) {
      navigate("/watch");
    } else if (newValue === 2) {
      navigate("/messages");
    } else if (newValue === 3) {
      navigate("/history");
    } else if (newValue === 4) {
      navigate("/settings");
    }
  };

  if (isWizardRoute) {
    return null;
  }

  const isDarkMode = theme.palette.mode === "dark";
  const activeNavColor = isDarkMode ? theme.colors.white : theme.colors.blue;
  const inactiveNavColor = theme.colors.blue;

  const homeIcon = value === 0
    ? <HomeRoundedIcon sx={{ fontSize: 28, color: activeNavColor }} />
    : <HomeOutlinedIcon sx={{ fontSize: 28, color: inactiveNavColor }} />;
  const watchIcon = value === 1
    ? <OndemandVideoRoundedIcon sx={{ fontSize: 30, color: activeNavColor }} />
    : <OndemandVideoOutlinedIcon sx={{ fontSize: 30, color: inactiveNavColor }} />;
  const messageIcon = value === 2
    ? <MailRoundedIcon sx={{ fontSize: 30, color: activeNavColor }} />
    : <MailOutlineRoundedIcon sx={{ fontSize: 30, color: inactiveNavColor }} />;
  const historyIcon = value === 3
    ? <HistoryRoundedIcon sx={{ fontSize: 30, color: activeNavColor }} />
    : <HistoryOutlinedIcon sx={{ fontSize: 30, color: inactiveNavColor }} />;
  const settingsValue = 4;
  const settingsIcon = value === settingsValue
    ? <SettingsRoundedIcon sx={{ fontSize: 28, color: activeNavColor }} />
    : <SettingsOutlinedIcon sx={{ fontSize: 28, color: inactiveNavColor }} />;
  const navActionSx = {
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: "none",
    px: 0.25,
  };

  const nav = (
    <Paper
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        boxShadow: "none",
        border: "none",
        height: APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS,
      }}
      elevation={0}
    >
      <BottomNavigation
        value={value}
        onChange={handleChange}
        sx={(theme) => ({
          height: APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS,
          pb: APP_SHELL_LAYOUT.SAFE_AREA_BOTTOM_CSS,
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          backgroundColor: theme.colors.bottomNavBg,
        })}
      >
        <BottomNavigationAction
          value={0}
          sx={navActionSx}
          icon={homeIcon}
        />
        <BottomNavigationAction
          value={1}
          sx={navActionSx}
          icon={watchIcon}
        />
        <BottomNavigationAction
          value={2}
          sx={navActionSx}
          icon={
            hasUnreadMessages ? (
              <Badge
                badgeContent={unreadBadge}
                overlap="circular"
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                sx={(theme) => ({
                  "& .MuiBadge-badge": {
                    backgroundColor: theme.colors.red,
                    color: theme.palette.common.white,
                    height: 22,
                    minWidth: 0,
                    padding: "0 8px",
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    borderRadius: "12px",
                    whiteSpace: "nowrap",
                    maxWidth: "none",
                    top: 3,
                    left: 23,
                    transform: "translate(0%, -40%)",
                    transformOrigin: "0% 50%",
                  },
                })}
              >
                {messageIcon}
              </Badge>
            ) : (
              messageIcon
            )
          }
        />
        <BottomNavigationAction
          value={3}
          sx={navActionSx}
          icon={historyIcon}
        />
        <BottomNavigationAction
          value={settingsValue}
          sx={navActionSx}
          icon={settingsIcon}
        />
      </BottomNavigation>
    </Paper>
  );

  if (typeof document === "undefined") {
    return nav;
  }

  return <Portal>{nav}</Portal>;
};

export default BottomNav;
